import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useTranslations } from '@/hooks/use-translations';
import { useForm } from '@inertiajs/react';
import { CheckCircle, Send, Star } from 'lucide-react';
import { useState } from 'react';

interface ReviewFormProps {
    bookSlug: string;
    bookTitle?: string;
    onSubmitSuccess?: () => void;
}

export function ReviewForm({ bookSlug, bookTitle, onSubmitSuccess }: ReviewFormProps) {
    const { t } = useTranslations();
    const [isSubmitted, setIsSubmitted] = useState(false);
    const [rating, setRating] = useState(0);
    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    
    // Use Inertia's useForm hook for form handling
    const form = useForm({
        reviewer_name: '',
        rating: 0,
        review_text: '',
        // Honeypot fields - should remain empty
        website: '',
        email_address: '',
        surname: '',
        phone: '',
        company: '',
    });

    const validateForm = (): boolean => {
        const errors: Record<string, string> = {};
        
        // Name validation
        if (!form.data.reviewer_name.trim()) {
            errors.reviewer_name = t('Please enter your name');
        } else if (form.data.reviewer_name.trim().length < 2) {
            errors.reviewer_name = t('Your name must be at least 2 characters');
        } else if (form.data.reviewer_name.length > 100) {
            errors.reviewer_name = t('Your name cannot exceed 100 characters');
        }
        
        // Rating validation
        if (rating === 0) {
            errors.rating = t('Please select a rating');
        }
        
        // Review text validation
        if (!form.data.review_text.trim()) {
            errors.review_text = t('Please write a review');
        } else if (form.data.review_text.trim().length < 20) {
            errors.review_text = t('Your review must be at least 20 characters');
        } else if (form.data.review_text.length > 1000) {
            errors.review_text = t('Your review cannot exceed 1000 characters');
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        
        // Update rating in form data
        form.data.rating = rating;
        
        if (!validateForm()) {
            return;
        }

        // Get the current locale and build the correct URL
        const locale = window.location.pathname.split('/')[1] || 'en';
        const bookSegment = locale === 'hr' ? 'knjiga' : 
                           locale === 'de' ? 'buch' : 
                           locale === 'it' ? 'libro' : 
                           locale === 'es' ? 'libro' : 
                           locale === 'fr' ? 'livre' : 
                           'book';
        
        const url = `/${locale}/${bookSegment}/${bookSlug}/reviews`;
        
        form.post(url, {
            preserveScroll: true,
            onSuccess: () => {
                setIsSubmitted(true);
                form.reset();
                setRating(0);
                setValidationErrors({});
                
                // Call success callback if provided
                onSubmitSuccess?.();
                
                // Reset form after 5 seconds
                setTimeout(() => {
                    setIsSubmitted(false);
                }, 5000);
            },
            onError: (errors) => {
                // Errors are handled by Inertia and displayed automatically
                console.error('Form errors:', errors);
            }
        });
    };

    if (isSubmitted) {
        return (
            <div className="rounded-lg border border-green-200 bg-green-50 p-6 text-center dark:border-green-800 dark:bg-green-950">
                <CheckCircle className="mx-auto mb-3 h-12 w-12 text-green-600 dark:text-green-400" />
                <h4 className="mb-2 text-lg font-medium text-green-900 dark:text-green-100">
                    {t('Thank you for your review!')}
                </h4>
                <p className="text-sm text-green-700 dark:text-green-300">
                    {t('Our team will review and publish your submission shortly')}
                </p>
            </div>
        );
    }

    return (
        <form onSubmit={handleSubmit} className="space-y-6">
            {/* Honeypot fields - hidden from users but visible to bots */}
            <div style={{ position: 'absolute', left: '-9999px' }} aria-hidden="true">
                <input
                    type="text"
                    name="website"
                    value={form.data.website}
                    onChange={(e) => form.setData('website', e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                />
                <input
                    type="email"
                    name="email_address"
                    value={form.data.email_address}
                    onChange={(e) => form.setData('email_address', e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                />
                <input
                    type="text"
                    name="surname"
                    value={form.data.surname}
                    onChange={(e) => form.setData('surname', e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                />
                <input
                    type="tel"
                    name="phone"
                    value={form.data.phone}
                    onChange={(e) => form.setData('phone', e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                />
                <input
                    type="text"
                    name="company"
                    value={form.data.company}
                    onChange={(e) => form.setData('company', e.target.value)}
                    tabIndex={-1}
                    autoComplete="off"
                />
            </div>
            
            <div className="grid gap-6 md:grid-cols-2">
                <div>
                    <label htmlFor="reviewer-name" className="mb-2 block text-sm font-medium">
                        {t('Your Name')} *
                    </label>
                    <Input
                        id="reviewer-name"
                        type="text"
                        value={form.data.reviewer_name}
                        onChange={(e) => {
                            form.setData('reviewer_name', e.target.value);
                            if (validationErrors.reviewer_name) {
                                setValidationErrors(prev => ({ ...prev, reviewer_name: '' }));
                            }
                        }}
                        placeholder={t('Enter your name')}
                        required
                        disabled={form.processing}
                        className="w-full"
                    />
                    {(validationErrors.reviewer_name || form.errors.reviewer_name) && (
                        <p className="text-sm text-red-500 mt-1">{validationErrors.reviewer_name || form.errors.reviewer_name}</p>
                    )}
                </div>
                
                <div>
                    <label className="mb-2 block text-sm font-medium">
                        {t('Your Rating')} *
                    </label>
                    <div className="flex items-center gap-2">
                        {[1, 2, 3, 4, 5].map((star) => (
                            <button
                                key={star}
                                type="button"
                                onClick={() => {
                                    setRating(star);
                                    if (validationErrors.rating) {
                                        setValidationErrors(prev => ({ ...prev, rating: '' }));
                                    }
                                }}
                                disabled={form.processing}
                                className="transition-all hover:scale-110 focus:outline-none disabled:cursor-not-allowed"
                                aria-label={`Rate ${star} stars`}
                            >
                                <Star
                                    className={`h-6 w-6 ${
                                        star <= rating
                                            ? 'fill-yellow-400 text-yellow-400'
                                            : 'fill-gray-200 text-gray-200 hover:fill-yellow-200 hover:text-yellow-200'
                                    }`}
                                />
                            </button>
                        ))}
                        {rating > 0 && (
                            <span className="ml-2 text-sm text-muted-foreground">
                                {rating === 1 && t('Poor')}
                                {rating === 2 && t('Fair')}
                                {rating === 3 && t('Good')}
                                {rating === 4 && t('Very Good')}
                                {rating === 5 && t('Excellent')}
                            </span>
                        )}
                    </div>
                    {(validationErrors.rating || form.errors.rating) && (
                        <p className="text-sm text-red-500 mt-1">{validationErrors.rating || form.errors.rating}</p>
                    )}
                </div>
            </div>

            <div>
                <label htmlFor="review-text" className="mb-2 block text-sm font-medium">
                    {t('Your Review')} *
                </label>
                <Textarea
                    id="review-text"
                    value={form.data.review_text}
                    onChange={(e) => {
                        form.setData('review_text', e.target.value);
                        if (validationErrors.review_text) {
                            setValidationErrors(prev => ({ ...prev, review_text: '' }));
                        }
                    }}
                    placeholder={t('Share your thoughts about this book')}
                    required
                    disabled={form.processing}
                    rows={5}
                    className="w-full resize-none"
                />
                {(validationErrors.review_text || form.errors.review_text) && (
                    <p className="text-sm text-red-500 mt-1">{validationErrors.review_text || form.errors.review_text}</p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                    {t('Please keep your review respectful and constructive')}
                </p>
            </div>

            <div className="flex items-center justify-between">
                <p className="text-xs text-muted-foreground">
                    * {t('Required fields')}
                </p>
                <Button
                    type="submit"
                    disabled={form.processing}
                    className="min-w-[140px]"
                >
                    {form.processing ? (
                        <>
                            <span className="mr-2 inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent"></span>
                            {t('Submitting...')}
                        </>
                    ) : (
                        <>
                            <Send className="mr-2 h-4 w-4" />
                            {t('Submit Review')}
                        </>
                    )}
                </Button>
            </div>
        </form>
    );
}